import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listPortalOrders, getPortalOrderFormData, bulkCreatePortalOrders,
} from "@/lib/portal.functions";
import { listOrderOperationalSummaries, type OrderSummaryLite, type OrderHealth } from "@/lib/orders/summary.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusPill } from "@/components/ui/status-pill";
import { toneFromHealth } from "@/lib/health-tone";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ExternalLink, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/orders")({ component: PortalOrders });

const PORTAL_HEALTH_DOT: Record<OrderHealth, string> = {
  ok: "bg-emerald-500",
  watch: "bg-amber-500",
  urgent: "bg-orange-600",
  blocked: "bg-red-600",
};
const PORTAL_HEALTH_LABEL: Record<OrderHealth, string> = {
  ok: "On track",
  watch: "We're watching this",
  urgent: "Needs attention",
  blocked: "There's an issue",
};

function PortalOrders() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");
  const qc = useQueryClient();

  const fetchOrders = useServerFn(listPortalOrders);
  const fetchForm = useServerFn(getPortalOrderFormData);
  const bulkFn = useServerFn(bulkCreatePortalOrders);
  const fetchSummaries = useServerFn(listOrderOperationalSummaries);
  const { data: payload, isLoading } = useQuery({
    queryKey: ["portal-orders"],
    queryFn: () => runServerFn(() => fetchOrders(), "Loading orders"),
  });
  const { data: form } = useQuery({
    queryKey: ["portal-order-form"],
    queryFn: () => runServerFn(() => fetchForm(), "Loading form"),
  });
  const skuByCode = new Map((form?.skus ?? []).map((s) => [s.sku_code.toLowerCase(), s.id]));
  const facilityByName = new Map((form?.facilities ?? []).map((f) => [f.name.toLowerCase(), f.id]));
  const orders = payload?.orders ?? [];
  const shipments = payload?.shipments ?? [];
  const orderIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const { data: summaries } = useQuery({
    queryKey: ["portal-orders-summaries", orderIds],
    enabled: orderIds.length > 0,
    queryFn: () => runServerFn(
      () => fetchSummaries({ data: { orderIds } }),
      "Loading order health",
    ),
  });
  const summaryById = useMemo(() => {
    const m = new Map<string, OrderSummaryLite>();
    for (const s of summaries ?? []) m.set(s.identity.id, s);
    return m;
  }, [summaries]);
  const trackingFor = (id: string) => shipments.find((s) => s.order_id === id)?.tracking_number ?? null;

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (status !== "all" && o.status !== status) return false;
      if (!ql) return true;
      return o.order_number?.toLowerCase().includes(ql) || (trackingFor(o.id) ?? "").toLowerCase().includes(ql);
    });
  }, [orders, q, status, shipments]);

  const trackingUrl = (carrier: string | null, tn: string) => {
    const c = (carrier ?? "").toLowerCase();
    if (c.includes("ups")) return `https://www.ups.com/track?tracknum=${encodeURIComponent(tn)}`;
    if (c.includes("fedex")) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`;
    if (c.includes("usps")) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tn)}`;
    if (c.includes("dhl")) return `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(tn)}`;
    return null;
  };

  return (
    <>
      <PortalPageHeader title="Orders" description="Outbound orders for your brand." actions={
        <div className="flex gap-2 flex-wrap">
          <CsvImportDialog
            trigger={<Button size="sm" variant="outline" disabled={!form?.customer}><Upload className="h-4 w-4 mr-1" />Import orders</Button>}
            title="Import orders"
            templateHeaders={[
              "order_number", "facility", "carrier", "service",
              "ship_name", "ship_company", "ship_street1", "ship_street2",
              "ship_city", "ship_state", "ship_postal", "ship_country",
              "ship_phone", "ship_email", "sku_code", "qty",
            ]}
            sampleRow={[
              "PO-2001", form?.facilities?.[0]?.name ?? "Main", "UPS", "ground",
              "Jane Doe", "", "123 Main St", "",
              "Austin", "TX", "78701", "US",
              "", "", form?.skus?.[0]?.sku_code ?? "SKU-001", "1",
            ]}
            validateRow={(r) => {
              if (!r.order_number) return { ok: false, error: "order_number required" };
              if (!r.sku_code || !skuByCode.has(r.sku_code.toLowerCase())) return { ok: false, error: `Unknown sku_code: ${r.sku_code}` };
              const qty = parseInt(r.qty, 10);
              if (!qty || qty < 1) return { ok: false, error: "qty must be ≥1" };
              if (!r.ship_name || !r.ship_street1 || !r.ship_city || !r.ship_state || !r.ship_postal || !r.ship_country) {
                return { ok: false, error: "ship_name/street1/city/state/postal/country required" };
              }
              if (r.ship_country.trim().length !== 2) return { ok: false, error: "ship_country must be ISO-2" };
              return { ok: true, value: r };
            }}
            onImport={async (rows) => {
              if (!form?.customer) return { inserted: 0, failed: rows.map((_, i) => ({ row: i + 2, error: "no customer" })) };
              // Group rows by order_number; pick facility from first row's `facility` (name) or default to first facility.
              const defaultFac = form.facilities?.[0]?.id;
              const groups = new Map<string, { facilityId: string; order: any }>();
              const failedPre: { row: number; error: string }[] = [];
              rows.forEach((r, i) => {
                const key = r.order_number.trim();
                const facId = r.facility ? facilityByName.get(r.facility.toLowerCase()) : defaultFac;
                if (!facId) { failedPre.push({ row: i + 2, error: `unknown facility: ${r.facility}` }); return; }
                if (!groups.has(key)) {
                  groups.set(key, {
                    facilityId: facId,
                    order: {
                      orderNumber: key,
                      carrier: r.carrier?.trim() || null,
                      serviceLevel: r.service?.trim() || null,
                      shipTo: {
                        name: r.ship_name.trim(),
                        company: r.ship_company?.trim() || null,
                        street1: r.ship_street1.trim(),
                        street2: r.ship_street2?.trim() || null,
                        city: r.ship_city.trim(),
                        state: r.ship_state.trim(),
                        postal_code: r.ship_postal.trim(),
                        country: r.ship_country.trim().toUpperCase(),
                        phone: r.ship_phone?.trim() || null,
                        email: r.ship_email?.trim() || null,
                      },
                      lines: [],
                    },
                  });
                }
                groups.get(key)!.order.lines.push({
                  skuId: skuByCode.get(r.sku_code.toLowerCase())!,
                  qty: parseInt(r.qty, 10),
                });
              });
              // Bulk fn requires single facility; group by facility too.
              const byFac = new Map<string, any[]>();
              for (const g of groups.values()) {
                if (!byFac.has(g.facilityId)) byFac.set(g.facilityId, []);
                byFac.get(g.facilityId)!.push(g.order);
              }
              let inserted = 0;
              const failed: { row: number; error: string }[] = [...failedPre];
              for (const [facId, ordersBatch] of byFac.entries()) {
                try {
                  const res = await runServerFn(() => bulkFn({
                    data: {
                      tenantId: form.customer!.tenant_id,
                      customerId: form.customer!.id,
                      facilityId: facId,
                      orders: ordersBatch,
                    },
                  }), "Importing orders");
                  inserted += res.inserted;
                  failed.push(...res.failed);
                } catch (e) {
                  failed.push({ row: 0, error: (e as Error).message });
                }
              }
              return { inserted, failed };
            }}
            onDone={() => qc.invalidateQueries({ queryKey: ["portal-orders"] })}
          />
          <Button size="sm" asChild>
            <Link to="/portal/orders/new"><Plus className="h-4 w-4 mr-1" />New order</Link>
          </Button>
        </div>
      } />
      <div className="page-container">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search order # or tracking…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="allocated">Allocated</SelectItem>
            <SelectItem value="picked">Picked</SelectItem>
            <SelectItem value="packed">Packed</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Order #</TableHead><TableHead>Carrier</TableHead><TableHead>Service</TableHead><TableHead>Tracking</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (<TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>)
            : filtered.length > 0 ? filtered.map((o) => {
              const tn = trackingFor(o.id);
              const url = tn ? trackingUrl(o.carrier, tn) : null;
              const s = summaryById.get(o.id);
              const health: OrderHealth = s?.row.health ?? "ok";
              const dot = PORTAL_HEALTH_DOT[health];
              return (
                <TableRow key={o.id}>
                  <TableCell className="font-mono font-medium">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${dot}`}
                        aria-label={`Health: ${PORTAL_HEALTH_LABEL[health]}`}
                        title={PORTAL_HEALTH_LABEL[health]}
                      />
                      <Link to="/portal/orders/$orderId" params={{ orderId: o.id }} className="text-primary hover:underline">
                        {o.order_number}
                      </Link>
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{o.carrier ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{o.service_level ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {tn ? (url ? (
                      <a href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                        {tn}<ExternalLink className="h-3 w-3" />
                      </a>
                    ) : tn) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><StatusPill tone={toneFromHealth(s?.row.health)}>{o.status}</StatusPill></TableCell>
                  <TableCell className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              );
            }) : (<TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No orders match.</TableCell></TableRow>)}
          </TableBody>
        </Table>
      </div>
      </div>
    </>
  );
}
